import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML, encodeHTML } from "@utils/CommonUtils";
import React, { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { post, put } from "~/Services";
import { magpHost } from "~/Utils";
import { useContractAdministrationProcessFlowChart } from "../Manual.hook";
import { fields } from "../manualConstants";

interface LocationState {
  record?: any;
}

export const EditContractAdministrationProcessFlowChart: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { record } = (location.state as LocationState) || {};

  const query = useQuery();
  const { openToast } = useNotification();

  const {
    data: processFlowChartData,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchData,
  } = useContractAdministrationProcessFlowChart(record?.id || "");

  const {
    data: processFlowChartDataById,
    isLoading: isLoadingProcessFlowChart,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (record?.id) {
      refetchData();
    }
  }, [record?.id]);
  useEffect(() => {
    if (query?.get("id")) {
      refetchDocument();
    }
  }, [query?.get("id")]);

  const ProcessFlowChartFormConfig: FormConfig = {
    initialValues: {
      title: processFlowChartDataById
        ? decodeHTML(processFlowChartDataById?.payload?.title || "")
        : processFlowChartData
          ? decodeHTML(processFlowChartData?.[0]?.title || "")
          : "",
      field_contract_process_title_ms: processFlowChartDataById
        ? decodeHTML(processFlowChartDataById?.payload?.field_contract_process_title_ms || "")
        : processFlowChartData
          ? decodeHTML(processFlowChartData?.[0]?.field_contract_process_title_ms || "")
          : "",
      field_contract_process_title_zh: processFlowChartDataById
        ? decodeHTML(processFlowChartDataById?.payload?.field_contract_process_title_zh || "")
        : processFlowChartData
          ? decodeHTML(processFlowChartData?.[0]?.field_contract_process_title_zh || "")
          : "",
      field_contract_flow_chart_des_en: processFlowChartDataById
        ? decodeHTML(processFlowChartDataById?.payload?.field_contract_flow_chart_des_en?.value || "")
        : processFlowChartData
          ? decodeHTML(processFlowChartData?.[0]?.field_contract_flow_chart_des_en || "")
          : "",
      field_contract_flow_chart_des_ms: processFlowChartDataById
        ? decodeHTML(processFlowChartDataById?.payload?.field_contract_flow_chart_des_ms?.value || "")
        : processFlowChartData
          ? decodeHTML(processFlowChartData?.[0]?.field_contract_flow_chart_des_ms || "")
          : "",
      field_contract_flow_chart_des_zh: processFlowChartDataById
        ? decodeHTML(processFlowChartDataById?.payload?.field_contract_flow_chart_des_zh?.value || "")
        : processFlowChartData
          ? decodeHTML(processFlowChartData?.[0]?.field_contract_flow_chart_des_zh || "")
          : "",
    },
    fields: fields.contractAdministrationProcessFlowChart,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let excludedKeys: any = [];
    // for (let key in data) {
    //   if (typeof data[key] === "string" && !excludedKeys.includes(key)) {
    //     data[key] = encodeHTML(data[key]);
    //   }
    // }
    let imageKeys: any = [
      "fbImage",
      "field_purchase_login_pop_image",
      "field_login_image",
      "filledImage",
      "googleImage",
      "showImage",
      "field_logout_image",
    ];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "MAGP");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }
    let descKeys: any = ["field_contract_flow_chart_des_en", "field_contract_flow_chart_des_ms", "field_contract_flow_chart_des_zh"];
    for (const key of descKeys) {
      try {
        data[key] = {
          value: data[key] || "",
          format: "full_html",
        };
      } catch (error) {}
    }

    const payload = {
      action: action,
      method: method,
      module: "Manual",
      subModule: `${query?.get("activeTab")}_${query?.get("subActiveTab")}_${query?.get(
        "thirdChildActiveTab"
      )}_${query?.get("fourthChildActiveTab")}`,
      type: "DEDICATED",
      application: "MAGP",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAGP",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...data,
        listId: processFlowChartData?.[0]?.listId,
        toggleId: processFlowChartData?.[0]?.toggleId,
        seqNumber: processFlowChartData?.[0]?.seqNumber,
        published: true,
      },
      status: status,
    };
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response:any) => {
          if (response.status === 'OK' && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else{
            navigate("/dedicated/status-information?activeTab=draft");
          }
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response:any) => {
          if (response.status === 'OK' && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else{
            navigate("/dedicated/status-information?activeTab=draft");
          }   
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("editId") || query.get("id") ? "Edit" : ""} Contract Administration Process Flow Chart`}
      showBack
    >
      <DynamicForm
        isLoading={isLoadingProcessFlowChart || drilldownLoading}
        config={ProcessFlowChartFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${magpHost}/api/contract-process-flowchart/${processFlowChartData?.[0]?.id}`,
            `UPDATE`,
            `PATCH`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
      />
    </ContentWrapper>
  );
};
